
import * as _ from "lodash"
import {theme} from "@/theme"
import {keyedDataUpdate} from "@/cube/keyedDataUpdate"
import {removeDimensionSortPrefix} from "@/cube/removeDimensionSortPrefix"
import {MetricSliced, MetricStore} from "@/cube/metric-data/MetricStore"
import {MemberSnapshotDimension, MemberSnapshotMeasure, Metric} from "@/cube"
import {toKey} from "@/cube/toKey"
import Vue from "vue"
import Component from "vue-class-component"
import {Prop} from "vue-property-decorator"
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import {dropShadowFilter} from "@/ui/chart/dropShadowFilter"
import {createChangeRateLabel} from "@/ui/chart/updateChangeRate"
import {disableInteraction} from "@/ui/chart/disableInteraction"
import {dropSortPrefixAdapter} from "@/ui/chart/dropSortPrefixAdapter"
import NoDataMessage from "@/ui/NoDataMessage.vue"
import {dimensionSettings} from "@/cube/settings"

// TODO Support nomination type per platform/provider
@Component({
  components: {NoDataMessage},
})
export default class BeneficiaryNominationChart extends Vue {
  @Prop()
  metricStore: MetricStore

  private hasData = true
  private ready = false
  private chart: am4charts.PieChart

  async mounted() {
    const metricData = this.metricStore.getSliced(
      MemberSnapshotMeasure.countMembers,
      [
        MemberSnapshotDimension.beneficiaryNominationType,
        MemberSnapshotDimension.beneficiaryNominationValidated,
      ]
    )

    this.chart = this.createChart()

    this.$watch(
      () => metricData.end,
      () => {
        if (metricData.end) {
          this.hasData = metricData.end.length > 0
          if (this.hasData) {
            this.chart.show()
            this.updateChartData(metricData)
            this.ready = true
          } else {
            this.chart.hide()
          }
        }
      },
      {immediate: true}
    )

    this.$watch(
      () => metricData.dateRange,
      () => {
        this.updateLegend(this.toData(metricData))
      }
    )
  }

  beforeDestroy() {
    this.chart.dispose()
  }

  updateChartData(
    metricSliced: MetricSliced<MemberSnapshotMeasure.countMembers>
  ) {
    console.log("Updating data", metricSliced)
    const nominationTypeSeries = this.chart.series.values[0]

    const data = this.toData(metricSliced)

    if (_.isEmpty(this.chart.data)) {
      this.chart.data = <any>metricSliced.end
      nominationTypeSeries.data = data
    } else {
      keyedDataUpdate(
        MemberSnapshotMeasure.countMembers,
        (row: any) =>
          toKey(row, [MemberSnapshotDimension.beneficiaryNominationType]),
        data,
        nominationTypeSeries.data
      )

      keyedDataUpdate(
        MemberSnapshotMeasure.countMembers,
        (row: any) =>
          toKey(row, [
            MemberSnapshotDimension.beneficiaryNominationType,
            MemberSnapshotDimension.beneficiaryNominationValidated,
          ]),
        metricSliced.end!,
        this.chart.data
      )

      this.chart.invalidateRawData()
    }

    this.updateLegend(data)
  }

  private toData(
    metricSliced: MetricSliced<MemberSnapshotMeasure.countMembers>
  ) {
    return _.chain(metricSliced.end)
      .groupBy(MemberSnapshotDimension.beneficiaryNominationType)
      .entries()
      .reduce((acc, [key, item]) => {
        const total = _.sumBy(item, metricSliced.metric)

        const changeRateSum =
          getChangeRateOrZero(metricSliced, `${key}::1|Yes`) +
          getChangeRateOrZero(metricSliced, `${key}::2|No`)

        acc.push({
          [MemberSnapshotDimension.beneficiaryNominationType]: removeDimensionSortPrefix(
            key
          ),
          [metricSliced.metric]: total,
          color: getColor(removeDimensionSortPrefix(key)),
          changeRate: changeRateSum,
        })

        return acc
      }, new Array<any>())
      .value()
  }

  private updateLegend(data: ReadonlyArray<any>) {
    const legend = <am4charts.Legend>(
      _.last(this.chart.chartAndLegendContainer.children.values)
    )

    legend.data = data.map(item => {
      const category = <string>(
        item[MemberSnapshotDimension.beneficiaryNominationType]
      )
      item.name = category
      item.fill = getColor(item.name)
      item.labelWithChangeRate = `${category} ${createChangeRateLabel(
        MemberSnapshotDimension.beneficiaryNominationType,
        item.name,
        {
          value: item.changeRate,
          unit: "%",
        }
      )}`

      return item
    })
  }

  private createChart() {
    const chart = am4core.create((<any>this.$refs).chart, am4charts.PieChart)
    chart.padding(-15, 0, 5, 0)
    chart.fontSize = 14
    chart.innerRadius = am4core.percent(55)
    chart.radius = am4core.percent(85)

    chart.series.push(createNominationTypeSeries())
    chart.series.push(createValidatedSeries())
    createLegend().parent = chart.chartAndLegendContainer

    const validatedLegend = new am4charts.Legend()
    validatedLegend.data.push({
      name: "Validated",
      fill: theme().positiveColor,
    })
    validatedLegend.y = am4core.percent(5)
    validatedLegend.contentAlign = "right"
    validatedLegend.labels.template.fontWeight = "lighter"
    disableInteraction(validatedLegend.itemContainers.template)

    chart.chartContainer.children.push(validatedLegend)

    return chart
  }
}

function createNominationTypeSeries() {
  const pieSeries = new am4charts.PieSeries()

  pieSeries.dataFields.value = MemberSnapshotMeasure.countMembers
  pieSeries.dataFields.category =
    MemberSnapshotDimension.beneficiaryNominationType

  pieSeries.labels.template.text = "{value.percent.formatNumber('###.')}%"
  pieSeries.labels.template.fill = am4core.color(theme().semiMutedColor)

  const sliceTemplate = pieSeries.slices.template
  sliceTemplate.propertyFields.fill = "color"
  sliceTemplate.fillOpacity = 0.25
  sliceTemplate.strokeWidth = 2
  sliceTemplate.strokeOpacity = 0.75
  disableInteraction(sliceTemplate)

  const shadowFilter = dropShadowFilter()
  shadowFilter.height = 120
  pieSeries.filters.push(shadowFilter)

  const hoverProperties = sliceTemplate.states.getKey("hover")!.properties
  hoverProperties.shiftRadius = 0
  hoverProperties.scale = 1

  return pieSeries
}

function createValidatedSeries() {
  const pieSeries = new am4charts.PieSeries()

  pieSeries.dataFields.value = MemberSnapshotMeasure.countMembers
  pieSeries.dataFields.category =
    MemberSnapshotDimension.beneficiaryNominationValidated

  pieSeries.radius = am4core.percent(2)
  pieSeries.innerRadius = am4core.percent(12)

  // Disable labels and ticks on inner circle
  pieSeries.labels.template.disabled = true
  pieSeries.ticks.template.disabled = true

  const sliceTemplate = pieSeries.slices.template

  sliceTemplate.fill = am4core.color(theme().positiveColor)
  sliceTemplate.strokeWidth = 0

  sliceTemplate.adapter.add("visible", (value, target) => {
    const dataItem = <am4charts.PieSeriesDataItem>target.dataItem
    return (
      dataItem.category !== "2|No" &&
      (<any>dataItem.dataContext)[
        MemberSnapshotDimension.beneficiaryNominationType
      ] !== "4|None"
    )
  })

  sliceTemplate.adapter.add("tooltipText", dropSortPrefixAdapter)
  disableInteraction(sliceTemplate)

  // Disable sliding out of slices
  const spriteState = sliceTemplate.states.getKey("hover")!.properties
  spriteState.shiftRadius = 0
  spriteState.scale = 1

  return pieSeries
}

function createLegend() {
  const legend = new am4charts.Legend()

  legend.paddingBottom = 10
  legend.labels.template.text = ""

  const labelTemplate = legend.valueLabels.template
  labelTemplate.propertyFields.html = "labelWithChangeRate"
  labelTemplate.text = "{name}"
  labelTemplate.fontWeight = "100"
  labelTemplate.paddingLeft = -1
  labelTemplate.textAlign = "start"

  legend.itemContainers.template.paddingLeft = 3
  disableInteraction(legend.itemContainers.template)

  const marker = legend.markers.template.children.getIndex(0)!
  marker.fillOpacity = 0.25
  marker.strokeWidth = 1
  marker.strokeOpacity = 1

  marker.adapter.add("stroke", (value, target) =>
    target.dataItem == null
      ? undefined
      : am4core.color((<any>target.dataItem.dataContext).fill)
  )

  return legend
}

function getChangeRateOrZero<T extends Metric>(
  metricSliced: MetricSliced<T>,
  key: string
): number {
  const changeRate = metricSliced.changeRateByKey[key]?.changeRate
  return _.isFinite(changeRate) ? changeRate! : 0
}

function getColor(category: string) {
  const {color} = dimensionSettings(
    MemberSnapshotDimension.beneficiaryNominationType,
    category
  )
  if (color === undefined) throw Error(`No color defined for ${category}`)
  else return color
}
